import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { callAppFunction, isSayWeeeApp } from '@weee-fe/utilities';
import { claimCouponLandingProduct } from '@/api/promotion';
import { getTargetCouponInfo } from '@/api/growth';
import { TargetCouponData } from '@/types/coupon';

interface PersonalizedCouponContextType {
  personalizedCouponData: TargetCouponData | null;
  loading: boolean;
  refreshPersonalizedCoupons: () => Promise<void>;
  claimPersonalizedCoupon: (productId: number) => Promise<void>;
}

const PersonalizedCouponContext = createContext<PersonalizedCouponContextType | undefined>(undefined);

export const PersonalizedCouponProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 1. 初始状态统一为 null
  const [personalizedCouponData, setPersonalizedCouponData] = useState<TargetCouponData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const router = useRouter();

  // 2. 数据抓取逻辑
  const fetchCouponStatus = useCallback(async () => {
    try {
      const res = await getTargetCouponInfo(['targeting_coupon']);
      if (res?.result) {
        const targetingCoupons =
          res?.object?.targetingCoupons?.find(item => item.refer_type === 'targeting_coupon') || null;
        setPersonalizedCouponData(targetingCoupons || null);
      }
    } catch (error) {
      console.error('Fetch Error', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 3. 首次拉取优惠券
  useEffect(() => {
    fetchCouponStatus();
  }, [fetchCouponStatus]);

  // 4. 路由：仅在真正切换页面文件路径时恢复展示；locale/query 差异不触发
  useEffect(() => {
    const handleComplete = () => {
      fetchCouponStatus();
    };

    router.events.on('routeChangeComplete', handleComplete);

    return () => {
      router.events.off('routeChangeComplete', handleComplete);
    };
  }, [fetchCouponStatus, router.events]);

  // 5. 乐观更新逻辑
  const claimPersonalizedCoupon = useCallback(
    async (productId: number) => {
      if (!personalizedCouponData) return;
      const idNum = Number(productId);
      if (!Number.isFinite(idNum)) return;
      const previousData = { ...personalizedCouponData };

      setPersonalizedCouponData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          discounts: prev.discounts.map(item => (Number(item.product_id) === idNum ? { ...item, claimed: true } : item))
        };
      });
      // 调用接口领取优惠券
      try {
        const res = await claimCouponLandingProduct({ product_id: productId });
        if (res.result) {
          if (isSayWeeeApp()) {
            callAppFunction('refreshNativePage');
          }
          // 与后端对齐领取态、文案等（useGetTargetProductCouponInfo 读的是本 store）
          await fetchCouponStatus();
        } else {
          throw new Error('Claim failed');
        }
      } catch (error) {
        setPersonalizedCouponData(previousData);
      }
    },
    [personalizedCouponData, fetchCouponStatus]
  );

  const contextValue = useMemo(
    () => ({
      personalizedCouponData,
      loading,
      refreshPersonalizedCoupons: fetchCouponStatus,
      claimPersonalizedCoupon
    }),
    [personalizedCouponData, loading, fetchCouponStatus, claimPersonalizedCoupon]
  );

  return <PersonalizedCouponContext.Provider value={contextValue}>{children}</PersonalizedCouponContext.Provider>;
};

/** 仅满足 Context，不拉券、不监听路由；用于 CMS 预览等不需要定向券能力的场景 */
export const PersonalizedCouponStubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const contextValue = useMemo<PersonalizedCouponContextType>(
    () => ({
      personalizedCouponData: null,
      loading: false,
      refreshPersonalizedCoupons: async () => {},
      claimPersonalizedCoupon: async () => {}
    }),
    []
  );

  return <PersonalizedCouponContext.Provider value={contextValue}>{children}</PersonalizedCouponContext.Provider>;
};

export const usePersonalizedCoupon = () => {
  const context = useContext(PersonalizedCouponContext);
  if (!context) throw new Error('usePersonalizedCoupon must be used within a PersonalizedCouponProvider');

  return {
    ...context
  };
};
